import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingBag, Plane, Truck, CheckCircle2, MessageCircle, Receipt, Star,
  ShieldAlert, MapPin,
} from "lucide-react";
import { StatusBadge, Spinner } from "../components/common";
import OrderTimeline from "../components/OrderTimeline";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import api, { apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { useAuth } from "../context/AuthContext";

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [dispute, setDispute] = useState({ reason: "Product not received", description: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => navigate("/orders")).finally(() => setLoading(false));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };

  if (loading || !order) return <Spinner />;
  const isBawainer = user?.role === "bawainer";
  const isTitiper = user?.role === "titiper";
  const s = order.status;

  const submitReceipt = () => act(() => api.post(`/orders/${id}/receipt`, {
    receipt_url: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&q=80",
    product_photo_url: order.product_image || "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&q=80",
    store_info: "Official Store",
  }), "Receipt diunggah — Item Purchased ✓");

  const submitReview = async (e) => {
    e.preventDefault();
    await act(() => api.post("/reviews", { order_id: id, rating: review.rating, comment: review.comment }), "Review terkirim ⭐");
    setReviewOpen(false);
  };
  const submitDispute = async (e) => {
    e.preventDefault();
    await act(() => api.post("/disputes", { order_id: id, reason: dispute.reason, description: dispute.description }), "Dispute dibuka");
    setDisputeOpen(false);
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-extrabold text-2xl text-navy">Order</h1>
        <StatusBadge status={s} />
      </div>

      <div className="bg-white rounded-3xl p-5 shadow-soft border border-black/5">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-cloud flex items-center justify-center"><ShoppingBag className="text-navy" /></div>
          <div>
            <div className="font-heading font-bold text-navy">{order.product_name}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={12} /> {order.destination}</div>
          </div>
        </div>
        <div className="mt-4 space-y-1 text-sm border-t border-black/5 pt-3">
          {[["Product", order.product_price], ["Service fee", order.service_fee], ["Platform fee", order.platform_fee], ["Delivery", order.delivery_fee]].map(([l, v]) => (
            <div key={l} className="flex justify-between text-muted-foreground"><span>{l}</span><span className="text-ink">{rupiah(v)}</span></div>
          ))}
          <div className="flex justify-between font-bold text-navy pt-2 border-t border-black/5 mt-1"><span>Total paid</span><span>{rupiah(order.total)}</span></div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-sky bg-sky-soft rounded-xl p-2">
          <ShieldAlert size={14} /> Pembayaran: <StatusBadge status={order.payment_status} />
        </div>
      </div>

      {/* timeline */}
      <div className="bg-white rounded-3xl p-5 shadow-soft border border-black/5">
        <div className="font-heading font-bold text-navy mb-4">Progress</div>
        <OrderTimeline status={s} />
      </div>

      {/* receipt */}
      {order.receipt_url && (
        <div className="bg-white rounded-3xl p-5 shadow-soft border border-black/5">
          <div className="font-heading font-bold text-navy flex items-center gap-2 mb-3"><Receipt size={18} className="text-lime-dark" /> Item Purchased ✓</div>
          <div className="grid grid-cols-2 gap-2">
            <img src={order.receipt_url} alt="receipt" className="rounded-xl h-32 w-full object-cover" />
            {order.product_photo_url && <img src={order.product_photo_url} alt="product" className="rounded-xl h-32 w-full object-cover" />}
          </div>
        </div>
      )}

      {/* delivery */}
      {order.delivery && (
        <div className="bg-white rounded-3xl p-5 shadow-soft border border-black/5">
          <div className="font-heading font-bold text-navy flex items-center gap-2"><Truck size={18} className="text-sky" /> Delivery · {order.delivery.method}</div>
          <div className="text-sm text-muted-foreground mt-1">{order.delivery.info}</div>
          <StatusBadge status={order.delivery.status} className="mt-2" />
        </div>
      )}

      {/* actions */}
      <div className="space-y-2">
        {isBawainer && s === "PAYMENT_COMPLETED" && (
          <button onClick={submitReceipt} disabled={busy} data-testid="upload-receipt-btn"
            className="w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 flex items-center justify-center gap-2 transition-colors"><Receipt size={17} /> Upload Receipt (Item Purchased)</button>
        )}
        {isBawainer && s === "ITEM_PURCHASED" && (
          <button onClick={() => act(() => api.patch(`/orders/${id}/status`, { status: "TRAVELING" }), "Status: On the way")} disabled={busy} data-testid="mark-traveling-btn"
            className="w-full rounded-full bg-navy text-white font-bold py-3 flex items-center justify-center gap-2 hover:bg-navy-700 transition-colors"><Plane size={17} /> Mark as Traveling</button>
        )}
        {isBawainer && s === "TRAVELING" && (
          <button onClick={() => act(() => api.patch(`/orders/${id}/status`, { status: "ARRIVED" }), "Status: Arrived")} disabled={busy} data-testid="mark-arrived-btn"
            className="w-full rounded-full bg-navy text-white font-bold py-3 flex items-center justify-center gap-2 hover:bg-navy-700 transition-colors"><MapPin size={17} /> Mark as Arrived</button>
        )}
        {isBawainer && s === "ARRIVED" && (
          <button onClick={() => act(() => api.post(`/orders/${id}/delivery`, { method: "Local delivery", info: "Dikirim via kurir lokal, estimasi 1-2 hari." }), "Delivery dibuat")} disabled={busy} data-testid="create-delivery-btn"
            className="w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 flex items-center justify-center gap-2 transition-colors"><Truck size={17} /> Create Delivery</button>
        )}
        {isBawainer && order.delivery && order.delivery.status !== "delivered" && s === "LOCAL_DELIVERY" && (
          <button onClick={() => act(() => api.patch(`/orders/${id}/delivery-status`, { status: "delivered" }), "Ditandai terkirim")} disabled={busy} data-testid="mark-delivered-btn"
            className="w-full rounded-full bg-navy text-white font-bold py-3 hover:bg-navy-700 transition-colors">Mark Delivered</button>
        )}
        {isTitiper && ["ARRIVED", "LOCAL_DELIVERY"].includes(s) && (
          <button onClick={() => act(() => api.post(`/orders/${id}/confirm`), "Order selesai! 🎉")} disabled={busy} data-testid="confirm-received-btn"
            className="w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 flex items-center justify-center gap-2 transition-colors"><CheckCircle2 size={17} /> Confirm Item Received</button>
        )}
        {s === "COMPLETED" && !order.review && (
          <button onClick={() => setReviewOpen(true)} data-testid="leave-review-btn"
            className="w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 flex items-center justify-center gap-2 transition-colors"><Star size={17} /> Leave a Review</button>
        )}
        {order.conversation_id && (
          <button onClick={() => navigate(`/chat/${order.conversation_id}`)} data-testid="order-chat-btn"
            className="w-full rounded-full bg-white border border-black/10 text-navy font-bold py-3 flex items-center justify-center gap-2"><MessageCircle size={17} /> Open Chat</button>
        )}
        {isTitiper && !["COMPLETED", "REFUNDED", "CANCELLED", "DISPUTED"].includes(s) && (
          <button onClick={() => setDisputeOpen(true)} data-testid="open-dispute-btn"
            className="w-full rounded-full text-red-600 font-semibold py-2 text-sm">Open a Dispute</button>
        )}
      </div>

      {/* review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-heading text-navy">Rate your experience</DialogTitle></DialogHeader>
          <form onSubmit={submitReview} className="space-y-3">
            <div className="flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setReview({ ...review, rating: n })} data-testid={`star-${n}`}>
                  <Star size={30} className={n <= review.rating ? "fill-lime text-lime" : "text-gray-300"} />
                </button>
              ))}
            </div>
            <Input data-testid="review-comment" value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} placeholder="Tulis review..." className="rounded-xl" />
            <button type="submit" disabled={busy} data-testid="submit-review" className="w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 transition-colors">Submit</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* dispute dialog */}
      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="dispute-dialog">
          <DialogHeader><DialogTitle className="font-heading text-navy">Open a Dispute</DialogTitle></DialogHeader>
          <form onSubmit={submitDispute} className="space-y-3">
            <select value={dispute.reason} onChange={(e) => setDispute({ ...dispute, reason: e.target.value })} data-testid="dispute-reason"
              className="w-full h-9 rounded-xl border border-input px-3 text-sm bg-transparent">
              {["Product not received", "Wrong product", "Damaged product", "Significantly different", "BAWAINER failed to purchase", "Other"].map((r) => <option key={r}>{r}</option>)}
            </select>
            <Input data-testid="dispute-desc" value={dispute.description} onChange={(e) => setDispute({ ...dispute, description: e.target.value })} placeholder="Jelaskan masalahnya" className="rounded-xl" />
            <button type="submit" disabled={busy} data-testid="submit-dispute" className="w-full rounded-full bg-red-500 text-white font-bold py-3">Submit Dispute</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
